import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Users, Sparkles, X } from 'lucide-react';
import { useTranslation } from '../../i18n/hooks/useTranslation';
import { useAuth } from '../../contexts/AuthContext';

interface EDMSegment {
  _id: string;
  name: string;
  description?: string;
  rules: any;
  estimatedSize?: number;
  isActive: boolean;
}

interface Role {
  _id: string;
  name: string;
}

interface ConstructionType {
  _id: string;
  name: string;
}

export const SegmentBuilder: React.FC<{ onUpdate?: () => void }> = ({ onUpdate }) => {
  const { t } = useTranslation();
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const [segments, setSegments] = useState<EDMSegment[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [constructionTypes, setConstructionTypes] = useState<ConstructionType[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingSegment, setEditingSegment] = useState<EDMSegment | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [calculatingSize, setCalculatingSize] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    rules: {
      roles: [] as string[],
      excludeRoles: [] as string[],
      minProjectsCreated: undefined as number | undefined,
      maxProjectsCreated: undefined as number | undefined,
      minTimesheetsCreated: undefined as number | undefined,
      maxTimesheetsCreated: undefined as number | undefined,
      minDaysSinceLastActivity: undefined as number | undefined,
      maxDaysSinceLastActivity: undefined as number | undefined,
      featuresUsed: [] as string[],
      featuresNotUsed: [] as string[],
      constructionTypes: [] as string[],
      accountAge: {
        min: undefined as number | undefined,
        max: undefined as number | undefined
      },
      subscriptionTier: [] as string[]
    }
  });

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  useEffect(() => {
    // Wait for auth to finish loading, then only fetch if authenticated
    if (authLoading) {
      return; // Still checking auth
    }
    
    if (isAuthenticated && user) {
      fetchSegments();
      fetchRoles();
      fetchConstructionTypes();
    } else {
      // Not authenticated - set empty state
      setSegments([]);
      setRoles([]);
      setConstructionTypes([]);
      setLoading(false);
    }
  }, [isAuthenticated, user, authLoading]);

  const fetchSegments = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setSegments([]);
        setLoading(false);
        return;
      }
      
      const response = await fetch(`${apiUrl}/api/edm/segments`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // Don't try to parse JSON if it's an auth error
      if (response.status === 401 || response.status === 403) {
        setSegments([]);
        setLoading(false);
        return;
      }
      
      if (!response.ok) {
        setSegments([]);
        setLoading(false);
        return;
      }
      
      const data = await response.json();
      if (data.success) {
        setSegments(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching segments:', error);
      setSegments([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setRoles([]);
        return;
      }
      
      const response = await fetch(`${apiUrl}/api/roles`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // Don't try to parse JSON if it's an auth error
      if (response.status === 401 || response.status === 403) {
        setRoles([]);
        return;
      }
      
      if (!response.ok) {
        setRoles([]);
        return;
      }
      
      const data = await response.json();
      if (data.success) {
        setRoles(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
      setRoles([]);
    }
  };

  const fetchConstructionTypes = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setConstructionTypes([]);
        return;
      }
      
      const response = await fetch(`${apiUrl}/api/construction-types`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // Don't try to parse JSON if it's an auth error
      if (response.status === 401 || response.status === 403) {
        setConstructionTypes([]);
        return;
      }
      
      if (!response.ok) {
        setConstructionTypes([]);
        return;
      }
      
      const data = await response.json();
      if (data.success) {
        setConstructionTypes(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching construction types:', error);
      setConstructionTypes([]);
    }
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('token');
      const url = editingSegment
        ? `${apiUrl}/api/edm/segments/${editingSegment._id}`
        : `${apiUrl}/api/edm/segments`;

      const response = await fetch(url, {
        method: editingSegment ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();
      if (data.success) {
        await fetchSegments();
        setEditingSegment(null);
        resetForm();
        onUpdate?.();
      } else {
        alert(data.message || 'Failed to save segment');
      }
    } catch (error) {
      console.error('Error saving segment:', error);
      alert('Failed to save segment');
    }
  };

  const handleCalculateSize = async (segmentId: string) => {
    try {
      setCalculatingSize(segmentId);
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/api/edm/segments/${segmentId}/calculate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        await fetchSegments();
      }
    } catch (error) {
      console.error('Error calculating segment size:', error);
    } finally {
      setCalculatingSize(null);
    }
  };

  const handleDelete = async (segmentId: string) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/api/edm/segments/${segmentId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        await fetchSegments();
        setShowDeleteConfirm(null);
        onUpdate?.();
      } else {
        alert(data.message || 'Failed to delete segment');
      }
    } catch (error) {
      console.error('Error deleting segment:', error);
      alert('Failed to delete segment');
    }
  };

  const handleAIGenerate = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/api/edm/segments/ai-recommendations`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ category: 'generic' })
      });

      const data = await response.json();
      if (data.success && data.data) {
        // Merge AI recommendations into formData rules
        setFormData(prev => ({
          ...prev,
          rules: {
            ...prev.rules,
            ...data.data
          }
        }));
      }
    } catch (error) {
      console.error('Error generating AI recommendations:', error);
      alert('Failed to generate AI recommendations');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      rules: {
        roles: [],
        excludeRoles: [],
        minProjectsCreated: undefined,
        maxProjectsCreated: undefined,
        minTimesheetsCreated: undefined,
        maxTimesheetsCreated: undefined,
        minDaysSinceLastActivity: undefined,
        maxDaysSinceLastActivity: undefined,
        featuresUsed: [],
        featuresNotUsed: [],
        constructionTypes: [],
        accountAge: {
          min: undefined,
          max: undefined
        },
        subscriptionTier: []
      }
    });
  };

  const handleEdit = (segment: EDMSegment) => {
    setEditingSegment(segment);
    setFormData({
      name: segment.name,
      description: segment.description || '',
      rules: segment.rules || formData.rules
    });
  };

  if (loading) {
    return <div className="text-center py-8">{t('common.loading') || 'Loading...'}</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
          {t('edm.segments.title') || 'Segments'}
        </h2>
        {!editingSegment && (
          <button
            onClick={() => {
              resetForm();
              setEditingSegment({} as EDMSegment);
            }}
            className="flex items-center space-x-2 px-4 py-2 bg-accent-600 text-white rounded-lg hover:bg-accent-700"
          >
            <Plus className="w-5 h-5" />
            <span>{t('edm.segments.createSegment') || 'Create Segment'}</span>
          </button>
        )}
      </div>

      {/* Segment Form */}
      {editingSegment && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {editingSegment._id ? t('edm.segments.editSegment') || 'Edit Segment' : t('edm.segments.createSegment') || 'Create Segment'}
            </h3>
            <div className="flex items-center space-x-2">
              <button
                onClick={handleAIGenerate}
                className="flex items-center space-x-1 px-3 py-1 text-sm text-accent-600 dark:text-accent-400 hover:text-accent-700"
              >
                <Sparkles className="w-4 h-4" />
                <span>{t('edm.segments.aiRecommendations') || 'AI Recommendations'}</span>
              </button>
              <button
                onClick={() => {
                  setEditingSegment(null);
                  resetForm();
                }}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('edm.segments.name') || 'Segment Name'}
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('edm.segments.description') || 'Description'}
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>

            {/* Role Filters */}
            <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <h4 className="font-medium text-gray-900 dark:text-white mb-3">
                {t('edm.segments.roleFilters') || 'Role Filters'}
              </h4>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('edm.segments.includeRoles') || 'Include Roles'}
                  </label>
                  <select
                    multiple
                    value={formData.rules.roles}
                    onChange={(e) => setFormData({
                      ...formData,
                      rules: {
                        ...formData.rules,
                        roles: Array.from(e.target.selectedOptions, option => option.value)
                      }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    size={5}
                  >
                    {roles.map(role => (
                      <option key={role._id} value={role._id}>{role.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('edm.segments.excludeRoles') || 'Exclude Roles'}
                  </label>
                  <select
                    multiple
                    value={formData.rules.excludeRoles}
                    onChange={(e) => setFormData({
                      ...formData,
                      rules: {
                        ...formData.rules,
                        excludeRoles: Array.from(e.target.selectedOptions, option => option.value)
                      }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    size={5}
                  >
                    {roles.map(role => (
                      <option key={role._id} value={role._id}>{role.name}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Usage Filters */}
            <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <h4 className="font-medium text-gray-900 dark:text-white mb-3">
                {t('edm.segments.usageFilters') || 'Usage Filters'}
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('edm.segments.minProjects') || 'Min Projects (30 days)'}
                  </label>
                  <input
                    type="number"
                    value={formData.rules.minProjectsCreated || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      rules: {
                        ...formData.rules,
                        minProjectsCreated: e.target.value ? parseInt(e.target.value) : undefined
                      }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('edm.segments.maxProjects') || 'Max Projects (30 days)'}
                  </label>
                  <input
                    type="number"
                    value={formData.rules.maxProjectsCreated || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      rules: {
                        ...formData.rules,
                        maxProjectsCreated: e.target.value ? parseInt(e.target.value) : undefined
                      }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('edm.segments.minTimesheets') || 'Min Timesheets (30 days)'}
                  </label>
                  <input
                    type="number"
                    value={formData.rules.minTimesheetsCreated || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      rules: {
                        ...formData.rules,
                        minTimesheetsCreated: e.target.value ? parseInt(e.target.value) : undefined
                      }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('edm.segments.maxTimesheets') || 'Max Timesheets (30 days)'}
                  </label>
                  <input
                    type="number"
                    value={formData.rules.maxTimesheetsCreated || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      rules: {
                        ...formData.rules,
                        maxTimesheetsCreated: e.target.value ? parseInt(e.target.value) : undefined
                      }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              </div>
            </div>

            {/* Construction Type Filters */}
            <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <h4 className="font-medium text-gray-900 dark:text-white mb-3">
                {t('edm.segments.constructionTypes') || 'Construction Types'}
              </h4>
              <select
                multiple
                value={formData.rules.constructionTypes}
                onChange={(e) => setFormData({
                  ...formData,
                  rules: {
                    ...formData.rules,
                    constructionTypes: Array.from(e.target.selectedOptions, option => option.value)
                  }
                })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                size={5}
              >
                {constructionTypes.map(ct => (
                  <option key={ct._id} value={ct._id}>{ct.name}</option>
                ))}
              </select>
            </div>

            <div className="flex space-x-4">
              <button
                onClick={handleSave}
                className="px-4 py-2 bg-accent-600 text-white rounded-lg hover:bg-accent-700"
              >
                {t('common.save') || 'Save'}
              </button>
              <button
                onClick={() => {
                  setEditingSegment(null);
                  resetForm();
                }}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                {t('common.cancel') || 'Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Segments List */}
      {!editingSegment && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
          <div className="p-6">
            {segments.length === 0 ? (
              <p className="text-center text-gray-500 dark:text-gray-400 py-8">
                {t('edm.segments.noSegments') || 'No segments found'}
              </p>
            ) : (
              <div className="space-y-3">
                {segments.map(segment => (
                  <div
                    key={segment._id}
                    className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <h3 className="font-medium text-gray-900 dark:text-white">{segment.name}</h3>
                        {segment.isActive ? (
                          <span className="px-2 py-1 text-xs rounded bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                            {t('common.active') || 'Active'}
                          </span>
                        ) : (
                          <span className="px-2 py-1 text-xs rounded bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
                            {t('common.inactive') || 'Inactive'}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        {segment.description || 'No description'}
                      </p>
                      <div className="flex items-center space-x-4 mt-2">
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          <Users className="w-4 h-4 inline mr-1" />
                          {segment.estimatedSize !== undefined
                            ? `${segment.estimatedSize} ${t('edm.segments.users') || 'users'}`
                            : t('edm.segments.sizeNotCalculated') || 'Size not calculated'}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleCalculateSize(segment._id)}
                        disabled={calculatingSize === segment._id}
                        className="p-2 text-accent-500 hover:text-accent-700 disabled:opacity-50"
                        title={t('edm.segments.calculateSize') || 'Calculate Size'}
                      >
                        <Users className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleEdit(segment)}
                        className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                        title={t('common.edit') || 'Edit'}
                      >
                        <Edit className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => setShowDeleteConfirm(segment._id)}
                        className="p-2 text-error-500 hover:text-error-700"
                        title={t('common.delete') || 'Delete'}
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-md w-full m-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {t('edm.segments.deleteConfirm') || 'Delete Segment?'}
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {t('common.confirmDelete') || 'Are you sure you want to delete this segment? This action cannot be undone.'}
            </p>
            <div className="flex space-x-4">
              <button
                onClick={() => handleDelete(showDeleteConfirm)}
                className="flex-1 px-4 py-2 bg-error-600 text-white rounded-lg hover:bg-error-700"
              >
                {t('common.delete') || 'Delete'}
              </button>
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                {t('common.cancel') || 'Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
